import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  cn,
} from '@design-system';
import { WagDisclosure } from './wag-disclosure';

import { Check, Loader2 } from 'lucide-react';

import type { FreemiumTierConfig } from '@shared';

interface TierCardProps {
  config: FreemiumTierConfig;
  clientFirstName: string;
  onSelect: () => void;
  isPending: boolean;
}

export function TierCard({ config, clientFirstName, onSelect, isPending }: TierCardProps) {
  const description = config.description.replace('{clientName}', clientFirstName);
  const isFeatured = !!config.recommended;

  return (
    <Card
      className={cn('relative flex h-full flex-col shadow-none', {
        'border-primary bg-primary text-primary-foreground': isFeatured,
      })}
    >
      {isFeatured && (
        <Badge
          variant="outline"
          className="text-primary border-primary bg-background absolute -top-3 left-1/2 -translate-x-1/2"
        >
          Most popular
        </Badge>
      )}

      <CardHeader>
        <CardTitle className={cn('text-base', { 'text-primary-foreground': isFeatured })}>
          {config.name}
        </CardTitle>
        <div className="mt-2 flex items-baseline gap-1.5">
          <span
            className={cn('text-primary text-4xl font-bold tracking-tight', {
              'text-primary-foreground': isFeatured,
            })}
          >
            ${config.priceUsd}
          </span>
          <span
            className={cn('text-muted-foreground text-sm', {
              'text-primary-foreground/70': isFeatured,
            })}
          >
            one-time
          </span>
        </div>
        <p
          className={cn('text-foreground mt-3 text-sm', {
            'text-primary-foreground/90': isFeatured,
          })}
        >
          {description}
        </p>
      </CardHeader>

      <CardContent className="flex-1">
        <ul className="space-y-2.5">
          {config.features.map(feature => (
            <li
              key={feature}
              className="flex items-start gap-2 text-sm"
            >
              <Check
                className={cn('text-primary mt-0.5 size-4 shrink-0', {
                  'text-primary-foreground': isFeatured,
                })}
              />
              <span
                className={cn('text-foreground', {
                  'text-primary-foreground/90': isFeatured,
                })}
              >
                {feature}
              </span>
            </li>
          ))}
        </ul>

        {config.isLicensed && (
          <WagDisclosure
            className={cn('text-muted-foreground mt-6 text-xs leading-relaxed', {
              'text-primary-foreground/70': isFeatured,
            })}
          />
        )}
      </CardContent>

      <CardFooter>
        <Button
          size="lg"
          onClick={onSelect}
          disabled={isPending}
          className={cn('w-full', { 'text-primary bg-white hover:bg-white/90': isFeatured })}
        >
          {isPending ? <Loader2 className="animate-spin" /> : config.ctaLabel}
        </Button>
      </CardFooter>
    </Card>
  );
}
